import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  listSeries,
  createSeries,
  updateSeries,
  deleteSeries,
  listSeriesEpisodes,
  createSeriesEpisode,
  listCharacters,
  createCharacter,
  formatDuration,
  timeAgoShort,
  ASPECT_RATIOS,
  type ContentSeries,
  type ContentProject,
} from '@/lib/contentStudio';

const STYLES = ['Documentaire', 'Cinématographique', 'Publicitaire', 'Pédagogique', 'Storytelling', 'Réseaux sociaux'];
const LANGUAGES = [
  { value: 'fr', label: 'Français' }, { value: 'en', label: 'Anglais' },
  { value: 'ar', label: 'Arabe' }, { value: 'es', label: 'Espagnol' }, { value: 'pt', label: 'Portugais' },
];

interface DraftState {
  name: string;
  description: string;
  style: string;
  language: string;
  aspect_ratio: string;
}

const EMPTY: DraftState = { name: '', description: '', style: STYLES[0], language: 'fr', aspect_ratio: '16:9' };

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Séries du studio : une série regroupe des épisodes liés qui partagent le
 * même style et la même direction artistique. À la création d'un épisode, on
 * peut hériter des personnages de l'épisode précédent.
 */
export default function ContentStudioSeriesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState<ContentSeries[]>([]);
  const [episodes, setEpisodes] = useState<Record<number, ContentProject[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [newEpisodeIdea, setNewEpisodeIdea] = useState('');
  const [addingEpisode, setAddingEpisode] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const srs = await listSeries(user.id);
      setSeries(srs);
      const map: Record<number, ContentProject[]> = {};
      await Promise.all(srs.map(async (s) => { map[s.id] = await listSeriesEpisodes(s.id); }));
      setEpisodes(map);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setCreating(true); setEditId(null); setDraft(EMPTY); };
  const openEdit = (s: ContentSeries) => {
    setEditId(s.id);
    setCreating(false);
    setDraft({
      name: s.name, description: s.description || '', style: s.style || STYLES[0],
      language: s.language || 'fr', aspect_ratio: s.aspect_ratio || '16:9',
    });
  };
  const closeForm = () => { setCreating(false); setEditId(null); setDraft(EMPTY); };

  const handleSave = async () => {
    if (!user?.id || !draft.name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (editId) {
        await updateSeries(editId, {
          name: draft.name.trim(), description: draft.description || null, style: draft.style,
          language: draft.language, aspect_ratio: draft.aspect_ratio,
        });
      } else {
        await createSeries(user.id, {
          name: draft.name.trim(), description: draft.description || null, style: draft.style,
          language: draft.language, aspect_ratio: draft.aspect_ratio,
        });
      }
      closeForm();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: ContentSeries) => {
    try {
      await deleteSeries(s.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de suppression');
    }
  };

  const handleAddEpisode = async (s: ContentSeries) => {
    if (addingEpisode) return;
    const idea = newEpisodeIdea.trim();
    if (!idea) { setError("Décrivez l'idée de l'épisode."); return; }
    setAddingEpisode(true);
    setError(null);
    try {
      const list = episodes[s.id] || [];
      const nextNumber = list.length + 1;
      const episode = await createSeriesEpisode(s, nextNumber, idea);
      if (!episode) throw new Error('Création impossible');
      // Hérite des personnages de l'épisode précédent (cohérence de la série).
      const previous = list[list.length - 1];
      if (previous) {
        const chars = await listCharacters(previous.id);
        await Promise.all(chars.map((c) => createCharacter(episode.id, {
          name: c.name, description: c.description, appearance: c.appearance,
          reference_image: c.reference_image, voice: c.voice,
        })));
      }
      setNewEpisodeIdea('');
      await load();
      navigate(`/dashboard/content-studio/project/${episode.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la création de l\u2019épisode');
    } finally {
      setAddingEpisode(false);
    }
  };

  if (!user?.id) {
    return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;
  }

  const formOpen = creating || !!editId;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <Link to="/dashboard/content-studio" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer">
        <i className="ri-arrow-left-line"></i>Content Studio
      </Link>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-stack-line text-accent-500"></i>Séries
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Regroupez des épisodes liés qui partagent le même style et la même direction artistique.
          </p>
        </div>
        {!formOpen && (
          <button type="button" onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Nouvelle série
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {formOpen && (
        <div className="mb-6 bg-background-50 border border-accent-200/70 rounded-xl p-4">
          <h3 className="text-sm font-bold text-foreground-900 mb-3">{editId ? 'Modifier la série' : 'Nouvelle série'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom de la série</label>
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Ex : 30 histoires africaines" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description</label>
              <textarea value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} rows={2} placeholder="Le fil conducteur commun à tous les épisodes…" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300 resize-none" />
            </div>
            <div className="relative">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Style commun</label>
              <select value={draft.style} onChange={(e) => setDraft((d) => ({ ...d, style: e.target.value }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 bottom-3 text-foreground-400 pointer-events-none"></i>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Langue</label>
                <select value={draft.language} onChange={(e) => setDraft((d) => ({ ...d, language: e.target.value }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                  {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 bottom-3 text-foreground-400 pointer-events-none"></i>
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Format</label>
                <select value={draft.aspect_ratio} onChange={(e) => setDraft((d) => ({ ...d, aspect_ratio: e.target.value }))} className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer">
                  {ASPECT_RATIOS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 bottom-3 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button type="button" onClick={closeForm} className="px-4 py-2 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
            <button type="button" onClick={handleSave} disabled={!draft.name.trim() || saving} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer">
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}Enregistrer
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>
      ) : series.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4"><i className="ri-stack-line text-2xl text-foreground-400"></i></div>
          <h4 className="text-sm font-semibold text-foreground-700 mb-1">Aucune série</h4>
          <p className="text-sm text-foreground-500 max-w-sm">Créez une série pour lier plusieurs épisodes autour d&apos;un même univers créatif.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {series.map((s) => {
            const eps = episodes[s.id] || [];
            const open = openId === s.id;
            return (
              <div key={s.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-11 h-11 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-stack-line text-accent-700 text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-bold text-foreground-900 truncate">{s.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px] text-foreground-500">
                      <span className="inline-flex items-center gap-1"><i className="ri-layout-grid-line"></i>{eps.length} épisode{eps.length > 1 ? 's' : ''}</span>
                      {s.style && <span className="inline-flex items-center gap-1"><i className="ri-palette-line"></i>{s.style}</span>}
                      <span className="inline-flex items-center gap-1"><i className="ri-aspect-ratio-line"></i>{s.aspect_ratio || '16:9'}</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => setOpenId(open ? null : s.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                    <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>{open ? 'Réduire' : 'Ouvrir'}
                  </button>
                  <button type="button" onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer" title="Modifier"><i className="ri-edit-line text-sm"></i></button>
                  <button type="button" onClick={() => handleDelete(s)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Supprimer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>

                {open && (
                  <div className="border-t border-background-200/70 p-4 bg-background-100/40">
                    {s.description && <p className="text-xs text-foreground-600 mb-4">{s.description}</p>}

                    {/* Épisodes */}
                    <div className="space-y-2 mb-4">
                      {eps.length === 0 ? (
                        <p className="text-xs text-foreground-400 py-2">Aucun épisode pour l&apos;instant.</p>
                      ) : (
                        eps.map((ep, i) => (
                          <div key={ep.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg px-3 py-2.5">
                            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800 text-xs font-bold flex-shrink-0">E{pad(ep.episode_number || i + 1)}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-foreground-800 truncate">{ep.name}</p>
                              <p className="text-[11px] text-foreground-400 flex items-center gap-2">
                                <span className="inline-flex items-center gap-1"><i className="ri-timer-line"></i>{formatDuration(ep.duration_seconds)}</span>
                                <span>· {timeAgoShort(ep.updated_at)}</span>
                              </p>
                            </div>
                            <button type="button" onClick={() => navigate(`/dashboard/content-studio/project/${ep.id}`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                              <i className="ri-folder-open-line"></i>Ouvrir
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Ajouter un épisode */}
                    <div className="flex items-center gap-2">
                      <input
                        value={openId === s.id ? newEpisodeIdea : ''}
                        onChange={(e) => setNewEpisodeIdea(e.target.value)}
                        placeholder={`Idée de l'épisode ${eps.length + 1}…`}
                        className="flex-1 min-w-0 px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
                      />
                      <button type="button" onClick={() => handleAddEpisode(s)} disabled={addingEpisode} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer">
                        {addingEpisode ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}Ajouter un épisode
                      </button>
                    </div>
                    <p className="text-[10px] text-foreground-400 mt-2 flex items-center gap-1.5">
                      <i className="ri-information-line"></i>Le nouvel épisode hérite du style de la série et des personnages de l&apos;épisode précédent.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}